import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { MapPinned, Plus, Trash2, Upload, Search, Download } from "lucide-react";
import { api, errorMessage } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const FIELDS = [["district", "District"], ["tehsil", "Tehsil"], ["ri", "RI"], ["village", "Village"]];
const EMPTY = { district: "", tehsil: "", ri: "", village: "" };

// Ported from Bolt "LocationDataManager": master list of District / Tehsil / RI / Village.
export function AdminLocations() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/admin/locations", { params: { q } }).then(({ data }) => setRows(data)).catch((err) => toast.error(errorMessage(err)));
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [q]); // eslint-disable-line react-hooks/exhaustive-deps

  const add = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/locations", form); toast.success("Location added"); setForm({ ...form, village: "" }); load(); } catch (err) { toast.error(errorMessage(err)); }
  };
  const remove = async (r) => { try { await api.delete(`/admin/locations/${r.id}`); setRows((rs) => rs.filter((x) => x.id !== r.id)); } catch (err) { toast.error(errorMessage(err)); } };
  const clearAll = async () => { if (!window.confirm("Delete ALL location rows?")) return; await api.delete("/admin/locations/all"); load(); toast.success("Location list cleared"); };
  const uploadCsv = async (f) => {
    const fd = new FormData(); fd.append("file", f); setBusy(true);
    try { const { data } = await api.post("/admin/locations/csv", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success(`Imported ${data.added} new locations from ${data.rows} rows`); load(); }
    catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };
  const exportCsv = () => {
    const csv = "\uFEFF" + ["district,tehsil,ri,village", ...rows.map((r) => FIELDS.map(([k]) => `"${(r[k] || "").replace(/"/g, '""')}"`).join(","))].join("\n");
    const a = document.createElement("a"); a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" })); a.download = "cgland-locations.csv"; a.click();
  };
  const suggestions = (k) => [...new Set(rows.map((r) => r[k]).filter(Boolean))];

  return (
    <div data-testid="admin-locations" className="grid lg:grid-cols-5 gap-6">
      <div className="lg:col-span-2 space-y-6">
        <form onSubmit={add} data-testid="location-add-form" className="rounded-2xl border bg-card p-7 space-y-4">
          <p className="data-label flex items-center gap-2"><MapPinned size={14} /> Add location</p>
          {FIELDS.map(([k, label]) => (
            <div key={k} className="space-y-1">
              <label htmlFor={`loc-${k}`} className="text-sm font-medium">{label}{k === "district" && " *"}</label>
              <Input id={`loc-${k}`} list={`locs-${k}`} data-testid={`location-input-${k}`} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} placeholder={`Enter ${label.toLowerCase()}`} required={k === "district"} className="h-10 rounded-xl" />
              <datalist id={`locs-${k}`}>{suggestions(k).map((v) => <option key={v} value={v} />)}</datalist>
            </div>
          ))}
          <Button type="submit" data-testid="location-add-button" className="w-full rounded-full bg-emerald-900 hover:bg-emerald-800 dark:bg-emerald-500 dark:text-slate-950"><Plus size={16} className="mr-1" /> Add location</Button>
        </form>
        <div className="rounded-2xl border bg-card p-7 space-y-3">
          <p className="data-label flex items-center gap-2"><Upload size={14} /> Upload location data (CSV)</p>
          <p className="text-xs text-muted-foreground">Header row with <span className="font-mono">district, tehsil, ri, village</span>. Duplicates are skipped.</p>
          <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" data-testid="location-csv-input" onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadCsv(f); e.target.value = ""; }} />
          <Button data-testid="location-csv-button" variant="outline" className="rounded-full" disabled={busy} onClick={() => fileRef.current?.click()}><Upload size={14} className="mr-1" /> {busy ? "Importing…" : "Choose CSV"}</Button>
        </div>
      </div>
      <div className="lg:col-span-3 rounded-2xl border bg-card overflow-hidden">
        <div className="p-4 border-b flex flex-col sm:flex-row sm:items-center gap-3">
          <div><p className="font-semibold">Complete location list</p><p className="text-xs text-muted-foreground" data-testid="location-count">{rows.length} rows · feeds the District › Tehsil › RI › Village dropdowns on the map</p></div>
          <div className="sm:ml-auto flex items-center gap-2">
            <div className="relative"><Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" /><Input data-testid="location-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search locations..." className="pl-9 h-9 rounded-full w-48" /></div>
            <Button variant="outline" size="sm" className="rounded-full" onClick={exportCsv} data-testid="location-export-button"><Download size={14} /></Button>
            <Button variant="outline" size="sm" className="rounded-full text-red-600" onClick={clearAll} data-testid="location-clear-all-button"><Trash2 size={14} /></Button>
          </div>
        </div>
        <div className="overflow-auto max-h-[560px]">
          <Table>
            <TableHeader><TableRow>{FIELDS.map(([k, l]) => <TableHead key={k}>{l}</TableHead>)}<TableHead /></TableRow></TableHeader>
            <TableBody>
              {rows.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-sm text-muted-foreground py-8" data-testid="location-empty">No locations yet — add one or upload a CSV.</TableCell></TableRow>}
              {rows.map((r) => (
                <TableRow key={r.id} data-testid={`location-row-${r.id}`}>
                  {FIELDS.map(([k]) => <TableCell key={k} className="text-sm">{r[k] || <span className="text-muted-foreground">—</span>}</TableCell>)}
                  <TableCell className="text-right"><button type="button" data-testid="location-delete-button" onClick={() => remove(r)} className="p-1.5 rounded-full text-red-600 hover:bg-red-50" aria-label="Delete"><Trash2 size={14} /></button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
